import { css, html, LitElement, nothing, TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import { getState, performAction } from "../services/routeService";
import { ActionReference, GameObjectReference, GameState } from "@shared/types";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = css`
        .game {
            height: 100%;
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto 1fr auto;
            grid-column-gap: 0px;
            grid-row-gap: 0px;
        }

        .title {
            text-align: center;
            margin-top: 10px;
        }

        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex-grow: 1;
            position: relative;
            margin-top: 10px;
        }

        .header img {
            width: 100%;
            height: auto;
        }

        .header img:nth-child(n + 2) {
            position: absolute;
        }

        .inventory-list {
            position: fixed;
            bottom: 0;
            right: 0;
            overflow: auto;
            color: white;
            border: 1px solid #ccc;
            border-radius: 5px;
            padding: 10px;
            margin: 10px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
        }

        .inventory-list p, .inventory-list li {
            margin: 0;
            line-height: 1.2;
        }

        .inventory-list .inventory-title {
            margin-top: 0;
        }

        .content {
            flex-grow: 1;
            overflow: auto;
            margin-top: 10px;
            padding: 0 10px;
            text-align: center;
        }

        .content p {
            margin: 0 0 10px 0;
        }

        .content p:last-of-type {
            margin: 0;
        }

        .footer {
            border-radius: 10px 10px 0 0;
            background-color: transparent;
            margin-top: 10px;
            display: flex;
            height: 105px;
            justify-content: center;
            align-items: center;
        }

        .footer .buttons {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            overflow: auto;
            padding: 10px 10px 0 10px;
        }

        .footer .button {
            background-color: #2196f3;
            border: 1px solid #0069c0;
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

        .footer .button.active,
        .footer .button:hover {
            background-color: #565656;
        }

        .reset-game {
            color: red;
        }
    `;

    private roomTitle?: string;
    private roomImages?: string[];
    private contentText?: string[];
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];
    private health?: number;
    private coins?: number;
    private keys?: number;
    private inventory?: Array<string>;

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    protected render(): TemplateResult {
        return html`
            <div class="game">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderInventory()}
                ${this.renderContent()} ${this.renderFooter()}
            </div>
        `;
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        //Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;
        this.health = state.health;
        this.coins = state.coins;
        this.keys = state.keys;
        this.inventory = state.inventory;

        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        this.requestUpdate();
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias),
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`
                <div class="title">${this.roomTitle}</div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png"/>`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderContent(): TemplateResult {
        return html`
            <div class="content">${this.contentText?.map((text) => html`<p>${text}</p>`)}</div>`;
    }

    private renderInventory(): TemplateResult {
        return html`
            <div class="inventory-list">
                <h2 class="inventory-title">Inventory</h2>
                <p>Health: ${this.health ? this.health : 0}</p>
                <p>Coins: ${this.coins ? this.coins : 0}</p>
                <p>Keys: ${this.keys ? this.keys : 0}</p>
                ${!this.inventory || this.inventory.length === 0
                    ? html`<p>You don't have any items</p>`
                    : html`
                    <ul>${this.inventory.map(item => {
                        const match: RegExpMatchArray | null = item.match(/"([^"]+)"/);
                        return html`<li>${match ? match[1] : item}</li>`;
                    })}</ul>`}
            </div>`;
    }

    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div class="button-container">
                        ${this.actionButtons?.map(
                            (button) =>
                                html`<a
                                    class="button ${this.selectedActionButton === button ? "active" : ""} ${button.label === "Reset Game" ? "reset-game" : ""}"
                                    @click=${(): void => void this.handleClickAction(button)}
                                >${button.label}</a
                                >`,
                        )}
                    </div>
                    <div class="button-container">
                        ${this.selectedActionButton
                            ? this.gameObjectButtons?.map(
                                (button) =>
                                    html`<a
                                        class="button ${this.selectedGameObjectButtons.has(button)
                                            ? "active"
                                            : ""}"
                                        @click=${(): void => void this.handleClickObject(button)}
                                    >${button.name}</a
                                    >`,
                            )
                            : nothing}
                    </div>
                </div>
            </div>
        `;
    }
}